import React from "react";
import moment from "moment";
import { Button, Card } from "@mui/material";
import { Divider } from "@monolith-forensics/monolith-ui";
import { MONOLITH_PERMISSIONS } from "../../../constants.js";
import { usePermissions } from "../../../hooks/usePermissions";
import { UNASSIGNED_FILTER_VALUE } from "../params/filter-constants";

// Shared Report Parameters card for every analytic report type (ENG-1417).
// The panel is never blank: every configured field renders a row, and a
// field with no saved values displays "All" (no filter, including rows with
// no value in that column). A blank time interval displays "All Time".

// Formats a list filter's saved values; absent/empty lists mean "All".
// `valueLabels` maps a stored scalar to the label the user picked, for fields
// whose value differs from its display (QA status stores "Yes"/"No" but the
// dropdown reads "Resolved"/"Not Resolved").
export const formatFilterValues = (
  values,
  unassignedLabel,
  displayField,
  valueLabels
) => {
  if (!Array.isArray(values) || values.length === 0) return "All";
  return values
    .map((v) => {
      if (v === UNASSIGNED_FILTER_VALUE) return unassignedLabel;
      if (v && typeof v === "object") {
        if (
          v.user_id === UNASSIGNED_FILTER_VALUE ||
          v[displayField] === UNASSIGNED_FILTER_VALUE
        )
          return unassignedLabel;
        return v[displayField || "full_name"];
      }
      return valueLabels?.[v] ?? v;
    })
    .join(", ");
};

// Field configs per report category. `single: true` fields hold one scalar
// (rendered as the value or "All"); list fields render via formatFilterValues.
export const CASES_PARAMS_FIELDS = [
  {
    key: "case_lead",
    label: "Case Lead",
    unassignedLabel: "(No Case Lead)",
    displayField: "full_name",
  },
  {
    key: "organization",
    label: "Organization",
    unassignedLabel: "(No Organization)",
  },
  { key: "case_types", label: "Case Types", unassignedLabel: "(No Case Type)" },
  {
    key: "case_statuses",
    label: "Case Status",
    unassignedLabel: "(No Case Status)",
  },
];

export const EVIDENCE_PARAMS_FIELDS = [
  {
    key: "organization",
    label: "Organization",
    unassignedLabel: "(No Organization)",
  },
  {
    key: "evidence_types",
    label: "Evidence Types",
    unassignedLabel: "(No Evidence Type)",
  },
  {
    key: "evidence_brands",
    label: "Evidence Providers",
    unassignedLabel: "(No Provider)",
  },
  {
    key: "case_lead",
    label: "Case Lead",
    unassignedLabel: "(No Case Lead)",
    displayField: "full_name",
  },
  { key: "case_types", label: "Case Types", unassignedLabel: "(No Case Type)" },
  {
    key: "case_statuses",
    label: "Case Status",
    unassignedLabel: "(No Case Status)",
  },
];

export const STORAGE_PARAMS_FIELDS = [
  {
    key: "storage_types",
    label: "Storage Types",
    unassignedLabel: "(No Storage Type)",
  },
  {
    key: "storage_sizes",
    label: "Storage Sizes",
    unassignedLabel: "(No Size Provided)",
  },
  {
    key: "storage_brands",
    label: "Storage Brands",
    unassignedLabel: "(No Brand)",
  },
  {
    key: "storage_models",
    label: "Storage Models",
    unassignedLabel: "(No Model)",
  },
];

export const ACQUISITIONS_PARAMS_FIELDS = [
  { key: "status", label: "Status", single: true },
];

export const QA_PARAMS_FIELDS = [
  {
    key: "examiner",
    label: "Examiner",
    unassignedLabel: "(No Examiner)",
    displayField: "full_name",
  },
  {
    key: "reviewer",
    label: "Reviewer",
    unassignedLabel: "(No Reviewer)",
    displayField: "full_name",
  },
  {
    key: "issue_type",
    label: "Issue Type",
    unassignedLabel: "(No Issue Type)",
  },
  {
    key: "status",
    label: "Status",
    valueLabels: { Yes: "Resolved", No: "Not Resolved" },
  },
];

export const TIME_ENTRY_PARAMS_FIELDS = [
  { key: "invoiced", label: "Invoiced", single: true },
  { key: "category", label: "Category", unassignedLabel: "(No Category)" },
  {
    key: "users",
    label: "Users",
    unassignedLabel: "(No User)",
    displayField: "full_name",
  },
];

export const FPR_PARAMS_FIELDS = [
  {
    key: "case_lead",
    label: "Case Lead",
    unassignedLabel: "(No Case Lead)",
    displayField: "full_name",
  },
  {
    key: "case_statuses",
    label: "Case Status",
    unassignedLabel: "(No Case Status)",
  },
  { key: "fpr_status", label: "FPR Status", single: true },
];

const Row = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      gap: "12px",
      paddingBottom: "5px",
    }}
  >
    <div style={{ flexShrink: 0 }}>{label}:</div>
    <div
      style={{
        flex: "1 1 auto",
        // min-width: 0 lets the flex item shrink below its content so the
        // ellipsis engages inside the card instead of overflowing and being
        // hard clipped by the card's overflow: hidden.
        minWidth: 0,
        textAlign: "right",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
      }}
      title={value || ""}
    >
      {value}
    </div>
  </div>
);

const ReportParametersPanel = ({
  reportInfo,
  editReportPopup,
  filterFields,
}) => {
  const { hasPermission } = usePermissions();
  const params = reportInfo?.report_parameters || {};

  // Dates and the time dimension only mean something when an interval bounds
  // the range; blank (and legacy "All Time") reports are unbounded, so those
  // rows are omitted even if a stale value was saved.
  const bounded = params.time_interval && params.time_interval !== "All Time";

  const rows = [
    { label: "Time Interval", value: params.time_interval || "All Time" },
  ];
  if (bounded && params.start_date) {
    rows.push({
      label: "Start Date",
      value: moment(params.start_date).format("MMMM DD, YYYY"),
    });
  }
  if (bounded && params.end_date) {
    rows.push({
      label: "End Date",
      value: moment(params.end_date).format("MMMM DD, YYYY"),
    });
  }
  if (bounded && params.timestamp_dimension) {
    rows.push({ label: "Timestamp", value: params.timestamp_dimension });
  }

  for (const field of filterFields || []) {
    const values = params[field.key];
    let value;
    if (field.single) {
      value = values || "All";
    } else if (field.format && Array.isArray(values) && values.length > 0) {
      // Per report override for fields whose saved values need a lookup to
      // display (e.g. time entry categories are stored as ids).
      value = field.format(values);
    } else {
      value = formatFilterValues(
        values,
        field.unassignedLabel,
        field.displayField,
        field.valueLabels
      );
    }
    rows.push({ label: field.label, value });
  }

  return (
    <Card
      style={{
        padding: "20px",
        textAlign: "left",
        flexGrow: 1,
        flexBasis: "10px",
        marginRight: 15,
      }}
    >
      <div
        style={{
          fontSize: "larger",
          display: "flex",
          justifyContent: "space-between",
          alignContent: "center",
        }}
      >
        <span style={{ display: "flex", alignItems: "center" }}>
          Report Parameters
        </span>
        {editReportPopup && (
          <Button
            size="small"
            onClick={() => editReportPopup.current.instance.show()}
            disabled={
              !hasPermission(MONOLITH_PERMISSIONS.METRICS_REPORTS_UPDATE)
            }
          >
            Edit
          </Button>
        )}
      </div>
      <Divider style={{ margin: "16px 0" }} />
      <div
        style={{
          // No fixed minWidth: the card shares the header row with two other
          // cards, so pinning a width forced the content past the card edge,
          // where MUI Card's overflow: hidden clipped the right aligned
          // values. Rows now shrink to the card width and ellipsize instead.
          overflowY: "auto",
          maxHeight: "175px",
          paddingRight: "10px",
        }}
      >
        {rows.map((row) => (
          <Row key={row.label} label={row.label} value={row.value} />
        ))}
      </div>
    </Card>
  );
};

export default ReportParametersPanel;
